import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgDotsHorizontalTriple } from '@actual-app/components/icons/v1';
import {
  SvgArrowButtonDown1,
  SvgArrowButtonUp1,
} from '@actual-app/components/icons/v2';
import { Popover } from '@actual-app/components/popover';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { useEnvelopeBudget } from '#components/budget/envelope/EnvelopeBudgetContext';
import { NotesButton } from '#components/NotesButton';
import { useLocale } from '#hooks/useLocale';
import { SheetNameProvider } from '#hooks/useSheetName';
import { useUndo } from '#hooks/useUndo';

import { BudgetMonthMenu } from './BudgetMonthMenu';
import { SummaryTiles } from './SummaryTiles';
import { ToBudget } from './ToBudget';

type BudgetSummaryProps = {
  month: string;
};
export const BudgetSummary = memo(({ month }: BudgetSummaryProps) => {
  const locale = useLocale();
  const {
    currentMonth,
    summaryCollapsed: collapsed,
    onBudgetAction,
    onToggleSummaryCollapse,
  } = useEnvelopeBudget();

  const [menuOpen, setMenuOpen] = useState(false);
  const triggerRef = useRef(null);
  const { showUndoNotification } = useUndo();

  function onMenuOpen() {
    setMenuOpen(true);
  }

  function onMenuClose() {
    setMenuOpen(false);
  }

  const prevMonthName = monthUtils.format(
    monthUtils.prevMonth(month),
    'MMM',
    locale,
  );

  const ExpandOrCollapseIcon = collapsed
    ? SvgArrowButtonDown1
    : SvgArrowButtonUp1;

  const displayMonth = monthUtils.format(month, "MMMM ''yy", locale);
  const { t } = useTranslation();

  return (
    <View
      data-testid="budget-summary"
      data-month={month}
      style={{
        flex: 1,
        containerType: 'inline-size',
        marginTop: 5,
        marginBottom: 12,
        cursor: 'default',
        '& .hover-visible': {
          opacity: 0,
          transition: 'opacity .25s',
        },
        '&:hover .hover-visible': {
          opacity: 1,
        },
      }}
    >
      <SheetNameProvider name={monthUtils.sheetForMonth(month)}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            marginBottom: 10,
            paddingLeft: 2,
          }}
        >
          <View
            style={{
              fontSize: 18,
              fontWeight: currentMonth === month ? 700 : 500,
            }}
          >
            {monthUtils.format(month, 'MMMM yyyy', locale)}
          </View>
          <Button
            variant="bare"
            aria-label={
              collapsed
                ? t('Expand month summary')
                : t('Collapse month summary')
            }
            className="hover-visible"
            onPress={onToggleSummaryCollapse}
          >
            <ExpandOrCollapseIcon
              width={13}
              height={13}
              // The margin is to make it the exact same size as the dots button
              style={{ color: theme.pageTextLight, margin: 1 }}
            />
          </Button>
          <View style={{ flex: 1 }} />
          <NotesButton
            id={`budget-${month}`}
            width={15}
            height={15}
            tooltipPosition="bottom right"
            defaultColor={theme.pageTextLight}
          />
          <View style={{ userSelect: 'none' }}>
            <Button
              ref={triggerRef}
              variant="bare"
              aria-label={t('Menu')}
              onPress={onMenuOpen}
            >
              <SvgDotsHorizontalTriple
                width={15}
                height={15}
                style={{ color: theme.pageTextLight }}
              />
            </Button>

            <Popover
              triggerRef={triggerRef}
              isOpen={menuOpen}
              onOpenChange={onMenuClose}
            >
              <BudgetMonthMenu
                onCopyLastMonthBudget={() => {
                  onBudgetAction(month, 'copy-last');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      "{{displayMonth}} budgets have all been set to last month's budgeted amounts.",
                      { displayMonth },
                    ),
                  });
                }}
                onSetBudgetsToZero={() => {
                  onBudgetAction(month, 'set-zero');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budgets have all been set to zero.',
                      { displayMonth },
                    ),
                  });
                }}
                onSetMonthsAverage={numberOfMonths => {
                  onBudgetAction(month, `set-${numberOfMonths}-avg`);
                  onMenuClose();
                  showUndoNotification({
                    message:
                      numberOfMonths === 12
                        ? t(
                            `${displayMonth} budgets have all been set to yearly average.`,
                          )
                        : t(
                            `${displayMonth} budgets have all been set to ${numberOfMonths} month average.`,
                          ),
                  });
                }}
                onCheckTemplates={() => {
                  onBudgetAction(month, 'check-templates');
                  onMenuClose();
                }}
                onApplyBudgetTemplates={() => {
                  onBudgetAction(month, 'apply-goal-template');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budget templates have been applied.',
                      { displayMonth },
                    ),
                  });
                }}
                onOverwriteWithBudgetTemplates={() => {
                  onBudgetAction(month, 'overwrite-goal-template');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} budget templates have been overwritten.',
                      { displayMonth },
                    ),
                  });
                }}
                onEndOfMonthCleanup={() => {
                  onBudgetAction(month, 'cleanup-goal-template');
                  onMenuClose();
                  showUndoNotification({
                    message: t(
                      '{{displayMonth}} end-of-month cleanup templates have been applied.',
                      { displayMonth },
                    ),
                  });
                }}
              />
            </Popover>
          </View>
        </View>

        <View
          style={{
            display: 'grid',
            gap: 8,
            gridTemplateColumns: collapsed
              ? 'minmax(0, 280px)'
              : '1.2fr repeat(4, 1fr)',
            // Narrow months (several shown side by side) stack the totals
            // in a 2x2 grid beside To Budget instead of one long row.
            '@container (max-width: 640px)': collapsed
              ? {}
              : { gridTemplateColumns: '1.2fr 1fr 1fr' },
          }}
        >
          <View
            style={{
              gridRow: 'span 2',
              '@container (min-width: 641px)': { gridRow: 'auto' },
              justifyContent: 'center',
              padding: '10px 16px',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: `color-mix(in srgb, ${theme.buttonPrimaryBackground} 10%, ${theme.cardBackground})`,
              boxShadow: styles.cardShadow,
            }}
          >
            <ToBudget
              prevMonthName={prevMonthName}
              month={month}
              onBudgetAction={onBudgetAction}
              isCollapsed={collapsed}
              style={{
                alignItems: 'flex-start',
                color: theme.pageTextSubdued,
                ...styles.smallText,
              }}
              amountStyle={{ fontSize: 26, fontWeight: 600 }}
            />
          </View>
          {!collapsed && <SummaryTiles prevMonthName={prevMonthName} />}
        </View>
      </SheetNameProvider>
    </View>
  );
});

BudgetSummary.displayName = 'EnvelopeBudgetSummary';
